import { useEffect, useMemo, useState } from "react";
import { useChatContext } from "stream-chat-react";

import {
  activeCollectorsSelector,
  useFetchCollectorsQuery,
} from "features/home/agencyPortal/homeAPI";
import UserBadge from "../UserBadge/UserBadge";
import { useAdminPanelFormState } from "./context/AdminPanelFormContext";
import { ValidationError } from "./ValidationError";

function ListContainer(props) {
  const { children } = props;
  const { errors, createChannelType } = useAdminPanelFormState();
  const showHeading = !createChannelType || createChannelType === "team";
  return (
    <div className="user-list__container">
      {showHeading && (
        <h2>
          <span>Add Members</span>
          <ValidationError errorMessage={errors.members} />
        </h2>
      )}
      <div className="user-list__header user-list__row">
        <div className="user-list__column-block">
          <p>User</p>
          <p className="user-list__column--aktos-role">Aktos Role</p>
        </div>
        <div className="user-list__column--checkbox">
          <p>Invite</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function UserItem({ user }) {
  const { handleMemberSelect } = useAdminPanelFormState();

  const title = user.name || user.email || user.id;

  return (
    <label htmlFor={user.id} title={title} className="user-list__row">
      <div className="user-list__column-block">
        <div className="user-list__column--user-data">
          <UserBadge image={user.image} name={title} userId={user.id} size={24} />
          <p className="user-item__name">{title}</p>
          <p className="user-item__id">({user.email})</p>
        </div>
        <p className="user-list__column--aktos-role">{user.aktos_role}</p>
      </div>
      <div className="user-list__column--checkbox">
        <input
          type="checkbox"
          name="members"
          id={user.id}
          value={user.id}
          onChange={handleMemberSelect}
        />
      </div>
    </label>
  );
}

const LOAD_STATE_NOTIFICATION = {
  empty: "No users found.",
  error: "Error loading, please refresh and try again.",
  loading: "Loading users...",
};

export default function UserList() {
  const { client, channel } = useChatContext();
  const { createChannelType } = useAdminPanelFormState();
  const [loadState, setLoadState] = useState(null);
  const [users, setUsers] = useState([]);

  const channelMembers = useMemo(
    () => (channel?.state?.members ? Object.keys(channel.state.members) : []),
    [channel?.state?.members],
  );

  const { activeCollectors: collectors } = useFetchCollectorsQuery(undefined, {
    selectFromResult: (result) => ({
      ...result,
      activeCollectors: activeCollectorsSelector(result),
    }),
  });

  // Create a map of collector UUIDs to IDs for easy access
  const collectorUuidToIdMap = useMemo(() => {
    const uuidToIdMap = {};
    collectors?.forEach((collector) => {
      uuidToIdMap[collector.uuid] = collector.id;
    });
    return uuidToIdMap;
  }, [collectors]);

  useEffect(() => {
    const getUsers = async () => {
      if (loadState) return;
      setLoadState("loading");

      try {
        const allUsers = [];
        let offset = 0;
        const limit = 100;
        let hasMore = true;

        while (hasMore) {
          // eslint-disable-next-line no-await-in-loop
          const response = await client.queryUsers({}, { id: 1 }, { limit, offset });

          if (response.users.length > 0) {
            allUsers.push(...response.users);
            offset += limit;
            // Add a small delay to prevent overwhelming the API
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => {
              setTimeout(resolve, 100);
            });
          } else {
            hasMore = false;
          }
        }

        // Filter out the current user and channel members and inactive users
        const filteredUsers = allUsers.filter(
          (user) => user.id !== client.userID && collectorUuidToIdMap?.[user.id],
        );

        if (filteredUsers.length) {
          setUsers(filteredUsers);
        } else {
          setLoadState("empty");
        }
      } catch {
        setLoadState("error");
      }

      setLoadState(null);
    };

    if (client) getUsers();
  }, [client, channelMembers, createChannelType]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ListContainer>
      {loadState ? (
        <div className="user-list__message">{LOAD_STATE_NOTIFICATION[loadState]}</div>
      ) : (
        Array.isArray(users) &&
        users.length > 0 &&
        users
          .filter((user) => user.id !== client.userID)
          .sort((a, b) => a.name?.localeCompare(b.name) || a.id.localeCompare(b.id))
          .map((user) => <UserItem key={user.id} user={user} />)
      )}
    </ListContainer>
  );
}
